// ============================================================================
// PsyWorkspace — рабочее место психолога (приложение «Психолог», kind='app').
//
// ОДИН экран: слева пациенты ядра «ГидраМедик» (medic_patients), справа —
// СВОДКА РЕЗУЛЬТАТОВ по всем пациентам (последние прохождения, ожидающие и
// просроченные назначения) и данные выбранного пациента: сессии
// (medic_encounters), заметки (psy_notes), заключения (psy_conclusions)
// и ТЕСТЫ/РЕЗУЛЬТАТЫ (банк методик + назначения + протоколы). Все списки —
// штатный AutoListForm (формы/права/поиск SSoT), формы записей — AutoObjectForm;
// тесты — PsyTestsTab. Никаких собственных копий списков/полей.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, CalendarDays, FileBarChart2, FileText, FlaskConical, HeartPulse, RefreshCw, Sparkles, UserRound } from 'lucide-react';
import { AutoListForm } from '@/components/autoforms/AutoListForm';
import { AutoObjectForm } from '@/components/autoforms/AutoObjectForm';
import { PatientCard } from '@/components/medic/PatientCard';
import { TabBar } from '@/components/ui/TabBar';
import { Button } from '@/components/ui/button';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { useAuthStore } from '@/store/authStore';
import { useSecurityAccess, PSY_ROLE } from '@/lib/securityRoles';
import { getSDK } from '@/store/sdkStore';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { formatValue } from '@/lib/format';
import { PsyTestsTab } from '@/components/psy/PsyTestsTab';
import { PsyPatientCabinet } from '@/components/psy/PsyPatientCabinet';
import { PsyResultsDashboard } from '@/components/psy/PsyResultsDashboard';
import { PsyReportsPanel } from '@/components/psy/PsyReportsPanel';
import { ENCOUNTERS, NOTES, CONCLUSIONS, PATIENTS } from '@/lib/psyWorkspace';
import type { ListFilter } from '@/types/formProperties';
import { useI18n } from '@/lib/i18n/I18nProvider';

type Patient = { id: string; name: string; phone?: string; birth_date?: string; gender?: string };

type Tab = 'summary' | 'sessions' | 'notes' | 'conclusions' | 'tests' | 'reports' | 'cabinet';

type Editor = {
  objectType: 'catalog' | 'document';
  objectName: string;
  objectId?: string;
  title: string;
  initial?: Record<string, unknown>;
};

// Вкладки рабочего места: иконка + КЛЮЧ перевода (SSoT текстов — lib/i18n).
const TABS: Array<{ id: Tab; Icon: typeof Activity; labelKey: string }> = [
  { id: 'summary', Icon: Activity, labelKey: 'psy.summary' },
  { id: 'sessions', Icon: CalendarDays, labelKey: 'psy.sessions' },
  { id: 'notes', Icon: FileText, labelKey: 'psy.notes' },
  { id: 'conclusions', Icon: Sparkles, labelKey: 'psy.conclusions' },
  { id: 'tests', Icon: FlaskConical, labelKey: 'psy.tests_results' },
  { id: 'reports', Icon: FileBarChart2, labelKey: 'psy.reports' },
  { id: 'cabinet', Icon: HeartPulse, labelKey: 'psy.patient_cabinet' },
];

function nowIso(): string {
  return new Date().toISOString().slice(0, 19);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function PsyWorkspace() {
  const { t } = useI18n();
  const currentUser = useAuthStore((s) => s.user);
  const access = useSecurityAccess();
  const isPsychologist = access.isAdmin || access.roles.includes(PSY_ROLE);
  const canManageTests = isPsychologist;
  const tabs = useMemo(
    () => TABS.map(({ id, Icon, labelKey }) => ({
      id,
      label: <><Icon className="h-3 w-3 inline mr-1" />{t(labelKey)}</>,
    })),
    [t],
  );
  const visibleTabs = useMemo(
    () => (isPsychologist ? tabs : tabs.filter((tabDef) => tabDef.id !== 'sessions' && tabDef.id !== 'reports')),
    [tabs, isPsychologist],
  );
  const [patient, setPatient] = useState<Patient | null>(null);
  const [cardOpen, setCardOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('summary');
  const [editor, setEditor] = useState<Editor | null>(null);
  const [patientsReady, setPatientsReady] = useState<boolean | null>(null);
  const [loadError, setLoadError] = useState('');
  const [listKey, setListKey] = useState(0);
  const [tabKey, setTabKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getSDK().metadata.listCatalogs({});
        const has = r.items.some((c) => String(c.code) === PATIENTS);
        if (!cancelled) setPatientsReady(has);
      } catch (e) {
        logErrorToJournal(e, 'Психолог: проверка ядра ГидраМедик');
        if (!cancelled) setLoadError(friendlyError(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!visibleTabs.some((tabDef) => tabDef.id === tab)) setTab('tests');
  }, [visibleTabs, tab]);

  const selectPatient = useCallback((row: Record<string, unknown>) => {
    const id = String(row.id || '');
    if (!id) return;
    const genderCode = String(row.gender || '');
    const gender = genderCode === 'male' ? t('psy.gender_male')
      : genderCode === 'female' ? t('psy.gender_female')
        : String(row.gender_presentation || genderCode);
    setPatient({
      id,
      name: String(row.name || row.fio || t('psy.patient_default')),
      phone: String(row.phone || ''),
      birth_date: String(row.birth_date || ''),
      gender,
    });
    setCardOpen(false);
    setTab('tests');
  }, []);

  /** Переход из сводки: открыть карточку пациента (диалог рабочего места). */
  const openSummaryPatient = useCallback((id: string, name: string) => {
    if (!id) return;
    setPatient({ id, name: name || t('psy.patient_default') });
    setCardOpen(true);
  }, []);

  /** Переход из сводки: открыть результаты пациента (вкладка «Тесты»). */
  const openSummaryResult = useCallback((id: string, name: string) => {
    if (!id) return;
    setPatient({ id, name: name || t('psy.patient_default') });
    setCardOpen(false);
    setTab('tests');
    setTabKey((k) => k + 1);
  }, []);

  const patientFilters = useMemo<ListFilter[]>(
    () => (patient ? [{ field: 'patient_id', condition: '=', value: patient.id }] : []),
    [patient],
  );

  const openCreate = (kind: Tab) => {
    if (!patient) return;
    if (kind === 'sessions') {
      if (!isPsychologist) return;
      setEditor({
        objectType: 'document', objectName: ENCOUNTERS, title: t('psy.sessions'),
        initial: {
          patient_id: patient.id,
          doctor_id: String(currentUser?.id || ''),
          encounter_type: 'psy_session',
          appointment_at: nowIso(),
          status: 'planned',
        },
      });
      return;
    }
    if (kind === 'notes') {
      setEditor({
        objectType: 'catalog', objectName: NOTES, title: t('psy.notes'),
        initial: { patient_id: patient.id, note_type: 'session', note_date: nowIso() },
      });
      return;
    }
    if (!isPsychologist) return;
    setEditor({
      objectType: 'catalog', objectName: CONCLUSIONS, title: t('psy.conclusions'),
      initial: { patient_id: patient.id, conclusion_date: today() },
    });
  };

  const openEdit = (kind: Tab, row: Record<string, unknown>) => {
    const id = String(row.id || '');
    if (!id) return;
    if (kind === 'sessions') {
      setEditor({ objectType: 'document', objectName: ENCOUNTERS, objectId: id, title: t('psy.session_title') });
    } else if (kind === 'notes') {
      setEditor({ objectType: 'catalog', objectName: NOTES, objectId: id, title: t('psy.note_title') });
    } else if (kind === 'conclusions') {
      setEditor({ objectType: 'catalog', objectName: CONCLUSIONS, objectId: id, title: t('psy.conclusion_title') });
    }
  };

  if (patientsReady === false) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="max-w-lg text-center space-y-2">
          <div className="text-sm font-medium">{t('psy.core_missing')}</div>
          <div className="text-xs text-muted-foreground">
            {t('psy.core_missing_hint')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 flex flex-col md:flex-row relative">
      {/* Пациенты (ядро) */}
      <aside className="w-full md:w-[300px] max-h-[45%] md:max-h-none shrink-0 border-b md:border-b-0 md:border-r flex flex-col min-h-0 bg-card/40">
        <div className="px-3 py-2.5 border-b flex items-center gap-2 shrink-0">
          <UserRound className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">{t('psy.patients')}</span>
          {isPsychologist && (
            <Button size="sm" variant="outline" className="h-7 px-2 ml-auto text-[11px]"
              onClick={() => setEditor({ objectType: 'catalog', objectName: PATIENTS, title: t('psy.new_patient') })}>
              + {t('psy.new_patient')}
            </Button>
          )}
          {isPsychologist && (
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title={t('psy.reports_hint')}
              onClick={() => { setCardOpen(false); setTab('reports'); }}>
              <FileBarChart2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        <div className="flex-1 min-h-0">
          <AutoListForm
            key={listKey}
            objectType="catalog"
            objectName={PATIENTS}
            viewMode="list"
            showToolbar
            singleClickOpen
            onRowDoubleClick={selectPatient}
            onCreate={isPsychologist
              ? () => setEditor({ objectType: 'catalog', objectName: PATIENTS, title: t('psy.new_patient') })
              : undefined}
          />
        </div>
      </aside>

      {/* Рабочая область: сводка по всем пациентам + данные выбранного пациента */}
      <main className="flex-1 min-w-0 min-h-0 flex flex-col">
        {patient && (
          <div className="px-3 py-2 border-b flex items-center gap-3 shrink-0 flex-wrap">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{patient.name}</div>
              <div className="text-[11px] text-muted-foreground truncate">
                {[patient.birth_date ? formatValue(patient.birth_date, '', 'date') : '',
                  patient.gender, patient.phone].filter(Boolean).join(' · ')}
              </div>
            </div>
            <Button size="sm" variant="outline" className="h-7 px-2 ml-auto text-[11px]"
              onClick={() => setCardOpen(true)}>
              Карточка
            </Button>
            <Button size="sm" variant="outline" className="h-7 px-2" title={t('common.refresh')}
              onClick={() => { setListKey((k) => k + 1); setTabKey((k) => k + 1); }}>
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
        {patient && (
          <div className="px-3 py-2 border-b shrink-0 flex items-center gap-2">
            <TabBar tabs={visibleTabs} active={tab} onChange={(id) => setTab(id as Tab)} group="psy-workspace" />
          </div>
        )}
        <div className="flex-1 min-h-0" key={`${tab === 'summary' ? 'summary' : patient?.id || ''}:${tab}:${tabKey}`}>
          {tab === 'reports' ? (
            <PsyReportsPanel
              patientId={patient?.id || ''}
              patientName={patient?.name || ''}
              onBack={() => setTab(patient ? 'tests' : 'summary')}
            />
          ) : tab === 'summary' ? (
            <PsyResultsDashboard
              onOpenPatient={openSummaryPatient}
              onOpenResult={openSummaryResult}
            />
          ) : !patient ? (
            <div className="h-full flex items-center justify-center p-6 text-center">
              <div className="max-w-md space-y-1">
                <div className="text-sm font-medium">{t('psy.select_patient')}</div>
                <div className="text-xs text-muted-foreground">
                  Слева — картотека ядра «ГидраМедик». Внутри карточки: сессии, заметки,
                  заключения, назначение методик и результаты тестов.
                </div>
              </div>
            </div>
          ) : tab === 'cabinet' ? (
            <PsyPatientCabinet
              patientId={patient.id}
              patientName={patient.name}
              canManage={isPsychologist}
            />
          ) : tab === 'tests' ? (
            <PsyTestsTab patientId={patient.id} patientName={patient.name} canManage={canManageTests} />
          ) : (
            <EntityList
              kind={tab}
              filters={patientFilters}
              canCreate={tab === 'notes' || isPsychologist}
              onCreate={() => openCreate(tab)}
              onOpen={(row) => openEdit(tab, row)}
            />
          )}
        </div>
      </main>

      {cardOpen && patient && (
        <UniversalWindow
          open
          onClose={() => setCardOpen(false)}
          title={`Карточка пациента: ${patient.name}`}
          width="w-[min(1100px,96vw)]"
          height="h-[min(88vh,94vh)]"
        >
          <PatientCard
            patientId={patient.id}
            patientName={patient.name}
            onOpenTests={() => {
              setCardOpen(false);
              setTab('tests');
              setTabKey((k) => k + 1);
            }}
            onOpenAppointment={(id) => {
              setCardOpen(false);
              setEditor({ objectType: 'document', objectName: ENCOUNTERS, objectId: id, title: t('psy.session_title') });
            }}
          />
        </UniversalWindow>
      )}
      {editor && (
        <AutoObjectForm
          objectType={editor.objectType}
          objectName={editor.objectName}
          objectId={editor.objectId}
          initialData={editor.initial}
          onClose={() => {
            setEditor(null);
            setListKey((k) => k + 1);
            setTabKey((k) => k + 1);
          }}
        />
      )}
      {loadError && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-xs text-destructive bg-card border border-destructive/40 rounded px-3 py-1.5">
          {loadError}
        </div>
      )}
    </div>
  );
}

/** Общий список вкладки пациента (сессии/заметки/заключения). */
function EntityList({ kind, filters, canCreate, onCreate, onOpen }: {
  kind: Tab;
  filters: ListFilter[];
  canCreate: boolean;
  onCreate: () => void;
  onOpen: (row: Record<string, unknown>) => void;
}) {
  const objectType = kind === 'sessions' ? 'document' : 'catalog';
  const objectName = kind === 'sessions' ? ENCOUNTERS : kind === 'notes' ? NOTES : CONCLUSIONS;
  // Сессии психолога — приёмы с типом «Психотерапевтическая сессия» (не смешиваем
  // с медицинскими визитами ядра; фильтр — SSoT сервера).
  const listFilters: ListFilter[] = kind === 'sessions'
    ? [...filters, { field: 'encounter_type', condition: '=', value: 'psy_session' }]
    : filters;
  return (
    <AutoListForm
      objectType={objectType}
      objectName={objectName}
      viewMode="list"
      showToolbar
      serverFilters={listFilters}
      onCreate={canCreate ? onCreate : undefined}
      onRowDoubleClick={onOpen}
      singleClickOpen
    />
  );
}
